import React from "react";
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from "@mui/material";
import { HtmlTooltip, StyledBlackInformationTypography } from "../../../components/commonComponents/StyledComponents/styled";
import TextInputField from "../../../components/commonComponents/TextInputs/TextInputField";
import { useDispatch, useSelector } from "react-redux";
import { deleteItems, updateStateData } from "../../../Store/actions";
import ActionButton from "../../../components/commonComponents/Buttons/ActionButton";
import { Close, Help, Save } from "@mui/icons-material";
import CloseButton from "../../../components/commonComponents/Buttons/CloseButton";
import {
    checkUserDetailsFromCookie,
    fetchCompanies,
    saveBrandInformation,
} from "../../../api_calls/userManagement";
import { toast } from "react-toastify";
import SingleSelect from "../../../components/commonComponents/Selectors/SingleSelect";
import SharedCampaignList from "./SharedCampaignList";
import Confirmation from "../../../components/commonComponents/Confirmation";

const AddBrandUser = ({ open, onClose, size, usedFor, userInfo, closeModal, cyreenBoolean }) => {
    const currentStateData = useSelector((state) => state.userSelections);
    const [userDetails, setUserDetails] = React.useState(userInfo);
    const adduserText = "Add Brand";
    const updateuserText = "Edit Brand";
    const [showCancelConfirmation, setShowCancelConfirmation] = React.useState(false);
    const [showSaveConfirmation, setShowSaveConfirmation] = React.useState(false);
    const contentRef = React.useRef(null);

    // fetch user detail from the cookies
    const checkUserDetail = checkUserDetailsFromCookie();
    let userDetailObj = {};
    if (checkUserDetail.success === true) {
        userDetailObj = checkUserDetail.user_detail;
    }
    const [userData, setUserData] = React.useState(userDetailObj);
    // this value is needed to select default company based on logged in user's company
    const [selectedCompanyOption, setSelectedCompanyOption] = React.useState({});
    const [fieldError, setFieldError] = React.useState({});

    // set selected company (case: when the company dropdown is not visible, we have select the company by default)
    const loggedInCompanyDtl = usedFor === "update" ? userInfo.multiple_accounts === true ?
        userInfo.assigned_company_details.some(dtl => dtl.id === userData?.assigned_company?.id) ?
            userInfo.assigned_company_details.filter(companyDtl => companyDtl.id === userData?.assigned_company?.id)
            :
            userInfo.assigned_company_details.sort((a, b) => a.name.localeCompare(b.name))
        :
        userInfo.assigned_company_details.sort((a, b) => a.name.localeCompare(b.name)) : {};

    const defaultCompanySelection =
        usedFor === "update"
            ? typeof userInfo.assigned_company_details !== "undefined"
                ? { id: loggedInCompanyDtl[0].id, label: loggedInCompanyDtl[0].name, ...loggedInCompanyDtl[0] }
                : ""
            : { id: userData?.assigned_company?.id, label: userData?.assigned_company?.name, ...userData?.assigned_company };

    const dispatchAction = useDispatch();

    const handleFieldChange = (user_data) => {
        setUserDetails(user_data);
        dispatchAction(updateStateData(user_data));
    };

    const handleDialogClose = () => {
        if (typeof currentStateData.user_interacted !== "undefined" && currentStateData.user_interacted === true) {
            setShowCancelConfirmation(true);
        } else {
            setShowCancelConfirmation(false);
            onClose();
        }
    }

    const handleSaveFunction = () => {
        const campaignIdsUpdated = typeof userInfo.campaign_ids !== "undefined" ? userInfo.campaign_ids : [];
        const campaignIdsFromState = typeof currentStateData.brand_campaign_selection !== "undefined" ? currentStateData.brand_campaign_selection : [];


        let changesMade = campaignIdsUpdated.length !== campaignIdsFromState.length;
        if (changesMade === true) {
            setShowSaveConfirmation(true);
        } else {
            saveBrandInfo();
        }
    }

    const saveBrandInfo = () => {
        setFieldError({});
        const userId = { id: typeof userDetails.id !== "undefined" ? userDetails.id : "" };
        saveBrandInformation(usedFor, userId).then((info) => {
            if (info.success === true) {
                const message = usedFor === "update" ? "User updated successfully" : "User added successfully";
                toast(message, { type: "success" });
                closeModal();
            } else {
                setFieldError(info.errorMessages);
            }
        });
    }

    React.useEffect(() => {
        fetchCompanies("users");
        if (defaultCompanySelection !== "") {
            setSelectedCompanyOption(defaultCompanySelection);
        }

        const selectedCompanyId = usedFor === "update" ? userInfo.workspace_id : defaultCompanySelection.id;

        dispatchAction(updateStateData(
            {
                ...userInfo,
                brand_campaign_selection: typeof defaultCompanySelection.campaign_ids !== "undefined" ? defaultCompanySelection.campaign_ids : [],
                cyreen_user: cyreenBoolean,
                selected_company_id: defaultCompanySelection.id,
                user_assigned_company: currentStateData.company_id_option_obj[defaultCompanySelection.id],
            }
        ));
    }, []);

    return (
        <Dialog
            open={open}
            onClose={(e) => {
                e.preventDefault();
            }}
            fullWidth={true}
            maxWidth={"lg"}
        >
            <DialogTitle> {usedFor === "update" ? updateuserText : adduserText}</DialogTitle>
            <DialogContent ref={contentRef}>
                <Stack direction={"row"} spacing={2}>
                    {/* ------------ company -------------- */}
                    {typeof userData.user_type !== "undefined" && ["cyreen", "branch"].includes(userData.user_type) && usedFor === "update" && (
                        <SingleSelect
                            enableFullWidth={true}
                            label={<span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                Company* <HtmlTooltip title={
                                    <Box width={200}>
                                        <Typography variant="body2">
                                            Manage shared campaigns for your company or any sub-company.
                                            Select the company for which you want to adjust the shared campaigns.
                                        </Typography>
                                    </Box>
                                }>
                                    <Help fontSize="small" />
                                </HtmlTooltip>
                            </span>}
                            selectionCheck={(option, value) => option.label === value.label}
                            sortOptions={true}
                            readOnlyFlag={typeof userInfo.assigned_company_details !== "undefined" && userInfo.assigned_company_details.length === 1 ? true : false}
                            options={
                                typeof userInfo.assigned_company_details !== "undefined" ? userInfo.assigned_company_details.map(companyDtl => {
                                    return { id: companyDtl.id, label: companyDtl.name, ...companyDtl };
                                }) : []
                            }
                            defaultVal={selectedCompanyOption}
                            onChangeCall={(selectedOption) => {
                                setSelectedCompanyOption(selectedOption);
                                let companyDetailToSave = {
                                    ...userDetails,
                                    user_interacted: true,
                                    user_assigned_company: currentStateData.company_id_option_obj[selectedOption.id],
                                    brand_campaign_selection: typeof selectedOption.campaign_ids !== "undefined" ? selectedOption.campaign_ids : [],
                                    selected_company_id: selectedOption.id,
                                };

                                handleFieldChange(companyDetailToSave);
                                // dispatchAction(deleteItems(["brand_campaign_selection"]));
                            }}
                            fieldError={
                                typeof fieldError.user_company !== "undefined" && fieldError.user_company !== ""
                                    ? fieldError.user_company
                                    : ""
                            }
                        />
                    )}
                    {/* --------- email ---------- */}
                    <TextInputField
                        fieldRequired={true}
                        elemId="email"
                        readOnlyFlag={usedFor === "update" ? true : false}
                        label="E-Mail"
                        fieldName="email"
                        showlabel={true}
                        defaultVal={typeof userDetails.email !== "undefined" ? userDetails.email : ""}
                        inputType={"text"}
                        onChangeCall={(e) => {
                            e.preventDefault();
                            const value = e.target.value;
                            if (value.includes(" ")) {
                                setFieldError({ ...fieldError, email: "E-Mail must not contain spaces" });
                            } else if (fieldError.email) {
                                const { email, ...rest } = fieldError;
                                setFieldError(rest);
                            }
                            handleFieldChange({
                                ...userDetails,
                                user_interacted: true,
                                email: value.replace(/\s/g, ""),
                            });
                        }}
                        fieldError={typeof fieldError.email !== "undefined" && fieldError.email !== "" ? fieldError.email : ""}
                    />
                </Stack>
                <Box height={10} />
                {/* --------- Campaign Selection --------- */}
                <StyledBlackInformationTypography variant="h6" sx={{ marginLeft: 0, fontWeight: "normal" }}>
                    Shared Campaign Reports
                </StyledBlackInformationTypography>
                <StyledBlackInformationTypography variant="body2" sx={{ marginLeft: 0, fontWeight: "normal" }}>
                    Select all campaign reports that this brand user should have access to. Additional settings for campaign-specific filters applied to the brands can be changed in the Brand Report settings.
                </StyledBlackInformationTypography>
                <Box height={5} />
                {
                    typeof selectedCompanyOption.id !== "undefined" && (
                        <SharedCampaignList
                            ownerId={usedFor === "update" ? selectedCompanyOption?.id : ""}
                            companyObj={selectedCompanyOption}
                            fieldError={typeof fieldError.campaign_id !== "undefined" && fieldError.campaign_id !== "" ? fieldError.campaign_id : ""}
                            contentRef={contentRef}
                        />
                    )
                }
            </DialogContent>
            <Box height={20} />
            <DialogActions>
                <Stack direction={"row"} spacing={2}>
                    <CloseButton onClick={handleDialogClose} label="Cancel" icon={<Close />} />
                    <ActionButton
                        onClick={(e) => {
                            e.preventDefault();
                            handleSaveFunction();
                        }}
                        label={usedFor === "update" ? "Update" : "Save"}
                        icon={<Save />}
                    />
                </Stack>
            </DialogActions>
            {/* ------- show save confirmation ----------- */}
            {
                showSaveConfirmation === true && (
                    <Confirmation
                        showConfirmationPopup={showSaveConfirmation}
                        closePopup={(e) => {
                            setShowSaveConfirmation(false);
                        }}
                        confirmationActionCall={() => {
                            setShowSaveConfirmation(false);
                            saveBrandInfo();
                        }}
                        confirmationMessage={
                            <>
                                <Typography variant="body2">
                                    You are about to share reports with external parties.
                                </Typography>
                                <Box height={10} />
                                <Typography variant="body2">
                                    Please confirm that all information is correct before proceeding.
                                </Typography>
                            </>
                        }
                        confirmationTitle={"Confirm Action"}
                    />
                )
            }
            {/* ------- show cancel confirmation ----------- */}
            {
                showCancelConfirmation === true && (
                    <Confirmation
                        showConfirmationPopup={showCancelConfirmation}
                        closePopup={(e) => {
                            setShowCancelConfirmation(false);
                        }}
                        confirmationActionCall={() => {
                            setShowCancelConfirmation(false);
                            onClose();
                        }}
                        confirmationMessage={
                            <>
                                <Typography variant="body2">
                                    Are you certain you want to discard all the changes you have made?
                                </Typography>
                            </>
                        }
                        confirmationTitle={"Confirm Action"}
                    />
                )
            }
        </Dialog>
    );
};

export default AddBrandUser;
